import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Archive, Copy, Edit, Eye, EyeOff, Plus, Search, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminCourseList() {
  const nav = useNavigate();
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/courses");
    setCourses(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function togglePublish(c) {
    try {
      await api.patch(`/admin/courses/${c.id}/publish`, { published: !(c.published ?? true) });
      toast.success(c.published === false ? "Course published" : "Course unpublished");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function del(c) {
    try {
      await api.delete(`/admin/courses/${c.id}`);
      toast.success(`Deleted "${c.title}"`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function duplicate(c) {
    try {
      const { data } = await api.post(`/admin/courses/${c.id}/duplicate`);
      toast.success(`Duplicated to "${data.title}"`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  async function toggleArchive(c) {
    try {
      await api.patch(`/admin/courses/${c.id}/archive`, { archived: !c.archived });
      toast.success(c.archived ? "Course restored" : "Course archived");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  const filtered = courses.filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()) || c.slug.includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="admin-course-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="admin-course-search" placeholder="Search courses..."
            className="pl-9 rounded-full h-11 bg-secondary/50 border-transparent" />
        </div>
        <Button onClick={() => nav("/app/admin/courses/new")} data-testid="admin-new-course-btn"
          className="rounded-full royal-gradient border-0 text-white btn-lift">
          <Plus className="h-4 w-4 mr-1.5" /> New course
        </Button>
      </div>

      <div className="rounded-3xl bg-card border border-border overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_120px_120px_120px_130px] gap-4 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
          <span>Course</span><span>Category</span><span>Lessons</span><span>Published</span><span className="text-right">Actions</span>
        </div>
        {filtered.map((c) => {
          const lessons = c.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0;
          const published = c.published ?? true;
          return (
            <div key={c.id} data-testid={`admin-course-row-${c.slug}`}
                 className="grid md:grid-cols-[1fr_120px_120px_120px_130px] gap-4 items-center px-5 py-3.5 border-b border-border last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-14 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                  {c.thumbnail && <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{c.title}</p>
                  <p className="text-xs text-muted-foreground truncate">/{c.slug}</p>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">{c.category}</span>
              <span className="text-xs text-muted-foreground">{lessons}</span>
              <div className="flex items-center gap-2">
                <Switch checked={published} onCheckedChange={() => togglePublish(c)}
                  data-testid={`admin-publish-toggle-${c.slug}`} />
                <span className={`text-xs ${published ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {published ? "Live" : "Draft"}
                </span>
              </div>
              <div className="flex items-center gap-1 justify-end">
                <Link to={`/app/admin/courses/${c.id}`}>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`admin-edit-${c.slug}`}>
                    <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={() => duplicate(c)} className="rounded-full"
                        data-testid={`admin-duplicate-${c.slug}`} title="Duplicate">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggleArchive(c)} className="rounded-full"
                        data-testid={`admin-archive-${c.slug}`} title={c.archived ? "Restore" : "Archive"}>
                  <Archive className={`h-3.5 w-3.5 ${c.archived ? "text-amber-500" : ""}`} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive"
                      data-testid={`admin-delete-${c.slug}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{c.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes the course, its modules, lessons and enrollments. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(c)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        data-testid={`admin-confirm-delete-${c.slug}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">No courses match.</div>}
      </div>
    </div>
  );
}
